import { Minus, Plus, Trash2 } from 'lucide-react'
import { useCart } from '@/context/CartContext'
import { formatPrice } from '@/utils/formatPrice'
import { imageThumb } from '@/utils/imageSrc'

/** @param {{ item: import('@/types').CartItem, compact?: boolean }} props */
export function CartItem({ item, compact = false }) {
  const { updateQuantity, removeItem } = useCart()

  return (
    <div className="flex gap-3 border-b border-charcoal-100 py-4 last:border-0">
      <img
        src={imageThumb(item.image)}
        alt={item.name}
        loading="lazy"
        decoding="async"
        className={
          compact
            ? 'h-16 w-16 flex-shrink-0 rounded-lg object-cover'
            : 'h-20 w-20 flex-shrink-0 rounded-lg object-cover sm:h-24 sm:w-24'
        }
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h4 className="line-clamp-2 text-sm font-semibold text-charcoal">{item.name}</h4>
            {(item.selectedSize || item.selectedColor) && (
              <p className="text-xs text-charcoal-600">
                {[item.selectedSize, item.selectedColor].filter(Boolean).join(' · ')}
              </p>
            )}
          </div>
          <button
            type="button"
            aria-label="Remove from cart"
            onClick={() => removeItem(item.id)}
            className="-mr-2 -mt-2 grid h-9 w-9 flex-shrink-0 place-items-center rounded-full text-charcoal-400 transition hover:bg-sand-100 hover:text-coral"
          >
            <Trash2 size={16} />
          </button>
        </div>
        <div className="mt-auto flex items-center justify-between gap-2 pt-2">
          <div className="flex items-center gap-0.5 rounded-full border border-charcoal-200 px-1 py-0.5">
            <button
              type="button"
              aria-label="Decrease quantity"
              onClick={() => updateQuantity(item.id, item.quantity - 1)}
              className="grid h-8 w-8 place-items-center rounded-full hover:bg-sand-200"
            >
              <Minus size={12} />
            </button>
            <span className="min-w-[2ch] text-center text-sm font-semibold">{item.quantity}</span>
            <button
              type="button"
              aria-label="Increase quantity"
              onClick={() => updateQuantity(item.id, item.quantity + 1)}
              disabled={item.quantity >= item.stock}
              className="grid h-8 w-8 place-items-center rounded-full hover:bg-sand-200 disabled:opacity-50"
            >
              <Plus size={12} />
            </button>
          </div>
          <span className="whitespace-nowrap font-semibold text-coral">{formatPrice(item.price * item.quantity)}</span>
        </div>
      </div>
    </div>
  )
}
